import React, { useState, useEffect } from 'react';
import { X, Save, RefreshCw, Plus, Trash2, GripVertical, AlertCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsManager = ({ onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('columns');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTabForInputs, setSelectedTabForInputs] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/config`);
      const data = await response.json();
      
      if (data.success) {
        setConfig(data.config);
      } else {
        setError('Erreur lors du chargement de la configuration');
      }
    } catch (err) {
      setError('Impossible de se connecter à l\'API');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/config/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Configuration sauvegardée avec succès !');
        if (onSave) onSave();
      } else {
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Impossible de sauvegarder la configuration');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // COLONNES
  const handleAddColumn = () => {
    const newColumn = {
      id: `column_${Date.now()}`,
      title: 'Nouvelle Colonne',
      order: (config?.columns?.length || 0) + 1,
      hidden: false,
    };
    setConfig(prev => ({
      ...prev,
      columns: [...(prev.columns || []), newColumn],
    }));
  };

  const handleUpdateColumn = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      columns: prev.columns.map(col => col.id === id ? { ...col, [field]: value } : col),
    }));
  };

  const handleDeleteColumn = (idToDelete) => {
    console.log('🗑️ Tentative de suppression colonne:', idToDelete);
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer cette colonne ?');
    console.log('✅ Confirmation:', confirmed);
    if (confirmed) {
      setConfig(prev => {
        const newColumns = prev.columns.filter(col => col.id !== idToDelete);
        console.log('📋 Nouvelles colonnes:', newColumns);
        return {
          ...prev,
          columns: newColumns,
        };
      });
    }
  };

  // TABS
  const handleAddTab = () => {
    const newTab = {
      id: `tab_${Date.now()}`,
      label: 'Nouvel Onglet',
      icon: '📋',
      order: (config?.modalTabs?.length || 0) + 1,
      hidden: false,
    };
    setConfig(prev => ({
      ...prev,
      modalTabs: [...(prev.modalTabs || []), newTab],
      formFields: {
        ...prev.formFields,
        [newTab.id]: [],
      },
    }));
  };

  const handleUpdateTab = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      modalTabs: prev.modalTabs.map(tab => tab.id === id ? { ...tab, [field]: value } : tab),
    }));
  };

  const handleDeleteTab = (idToDelete) => {
    console.log('🗑️ Tentative de suppression tab:', idToDelete);
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer cet onglet ?');
    console.log('✅ Confirmation:', confirmed);
    if (confirmed) {
      setConfig(prev => {
        const newFormFields = { ...prev.formFields };
        delete newFormFields[idToDelete];
        const newTabs = prev.modalTabs.filter(tab => tab.id !== idToDelete);
        console.log('📋 Nouveaux tabs:', newTabs);
        return {
          ...prev,
          modalTabs: newTabs,
          formFields: newFormFields,
        };
      });
    }
  };

  // INPUTS
  const handleAddInput = (tabId) => {
    const newInput = {
      id: `input_${Date.now()}`,
      label: 'Nouveau Champ',
      type: 'text',
      required: false,
      hidden: false,
    };
    setConfig(prev => ({
      ...prev,
      formFields: {
        ...prev.formFields,
        [tabId]: [...(prev.formFields[tabId] || []), newInput],
      },
    }));
  };

  const handleUpdateInput = (tabId, inputId, field, value) => {
    setConfig(prev => ({
      ...prev,
      formFields: {
        ...prev.formFields,
        [tabId]: prev.formFields[tabId].map(input => input.id === inputId ? { ...input, [field]: value } : input),
      },
    }));
  };

  const handleDeleteInput = (tabId, inputIdToDelete) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce champ ?')) {
      setConfig(prev => ({
        ...prev,
        formFields: {
          ...prev.formFields,
          [tabId]: prev.formFields[tabId].filter(input => input.id !== inputIdToDelete),
        },
      }));
    }
  };

  // STATUS
  const handleUpdateStatusMapping = (statusBo, columnId) => {
    setConfig(prev => ({
      ...prev,
      statusMapping: {
        ...prev.statusMapping,
        mappings: {
          ...prev.statusMapping.mappings,
          [statusBo]: columnId,
        },
      },
    }));
  };

  const handleAddStatusMapping = (statusBo, columnId) => {
    if (statusBo && columnId) {
      handleUpdateStatusMapping(statusBo, columnId);
    }
  };

  const handleDeleteStatusMapping = (statusBoToDelete) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce mapping ?')) {
      setConfig(prev => {
        const newMappings = { ...prev.statusMapping.mappings };
        delete newMappings[statusBoToDelete];
        return {
          ...prev,
          statusMapping: {
            ...prev.statusMapping,
            mappings: newMappings,
          },
        };
      });
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-card rounded-xl p-8 border border-border">
          <RefreshCw className="w-8 h-8 text-[#6366f1] animate-spin mx-auto" />
          <p className="text-foreground mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-border flex flex-col shadow-2xl">
        <div className="border-b border-border bg-sidebar px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">⚙️ Gestion de la Configuration</h2>
            <p className="text-sm text-muted mt-1">Personnalisez votre tableau Kanban</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-card-hover rounded-lg transition-colors text-muted hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        <div className="border-b border-border bg-sidebar px-6">
          <div className="flex gap-4">
            <button onClick={() => setActiveTab('columns')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'columns' ? 'border-[#6366f1] text-foreground' : 'border-transparent text-muted hover:text-foreground'}`}>
              📋 Colonnes ({config.columns?.length || 0})
            </button>
            <button onClick={() => setActiveTab('tabs')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'tabs' ? 'border-[#6366f1] text-foreground' : 'border-transparent text-muted hover:text-foreground'}`}>
              🗂️ Tabs ({config.modalTabs?.length || 0})
            </button>
            <button onClick={() => setActiveTab('inputs')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'inputs' ? 'border-[#6366f1] text-foreground' : 'border-transparent text-muted hover:text-foreground'}`}>
              📝 Inputs
            </button>
            <button onClick={() => setActiveTab('status')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'status' ? 'border-[#6366f1] text-foreground' : 'border-transparent text-muted hover:text-foreground'}`}>
              🔢 Status ({Object.keys(config.statusMapping?.mappings || {}).length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'columns' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted">Gérez les colonnes. Switch = masquer/afficher</p>
                <button onClick={handleAddColumn} className="flex items-center gap-2 px-4 py-2 bg-[#6366f1] hover:bg-[#5558e3] text-white rounded-lg text-sm">
                  <Plus className="w-4 h-4" />Ajouter
                </button>
              </div>
              <div className="space-y-2">
                {config.columns?.sort((a, b) => a.order - b.order).map((column, index) => (
                  <div key={`column-${index}-${column.id}`} className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
                    <GripVertical className="w-5 h-5 text-muted" />
                    <div className="flex-1 grid grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-muted">ID</label>
                        <input type="text" value={column.id} onChange={(e) => handleUpdateColumn(column.id, 'id', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-muted">Titre</label>
                        <input type="text" value={column.title} onChange={(e) => handleUpdateColumn(column.id, 'title', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-muted">Ordre</label>
                        <input type="number" value={column.order} onChange={(e) => handleUpdateColumn(column.id, 'order', parseInt(e.target.value))} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                      </div>
                      <div className="flex items-end justify-between gap-2">
                        <label className="flex items-center gap-2">
                          <button onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleUpdateColumn(column.id, 'hidden', !column.hidden);
                          }} className={`relative inline-flex h-6 w-11 items-center rounded-full ${column.hidden ? 'bg-gray-600' : 'bg-[#00bcd4]'}`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${column.hidden ? 'translate-x-1' : 'translate-x-6'}`} />
                          </button>
                          {column.hidden ? <EyeOff className="w-4 h-4 text-muted" /> : <Eye className="w-4 h-4 text-[#00bcd4]" />}
                        </label>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDeleteColumn(column.id);
                        }} className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'tabs' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted">Gérez les onglets de la modal</p>
                <button onClick={handleAddTab} className="flex items-center gap-2 px-4 py-2 bg-[#6366f1] hover:bg-[#5558e3] text-white rounded-lg text-sm">
                  <Plus className="w-4 h-4" />Ajouter
                </button>
              </div>
              {!config.modalTabs || config.modalTabs.length === 0 ? (
                <div className="text-center py-8 text-muted">Aucun onglet</div>
              ) : (
                <div className="space-y-2">
                  {config.modalTabs.sort((a, b) => a.order - b.order).map((tab, index) => (
                    <div key={`tab-${index}-${tab.id}`} className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
                      <GripVertical className="w-5 h-5 text-muted" />
                      <div className="flex-1 grid grid-cols-5 gap-4">
                        <div>
                          <label className="text-xs text-muted">ID</label>
                          <input type="text" value={tab.id} onChange={(e) => handleUpdateTab(tab.id, 'id', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-muted">Label</label>
                          <input type="text" value={tab.label} onChange={(e) => handleUpdateTab(tab.id, 'label', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-muted">Icône</label>
                          <input type="text" value={tab.icon} onChange={(e) => handleUpdateTab(tab.id, 'icon', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-muted">Ordre</label>
                          <input type="number" value={tab.order} onChange={(e) => handleUpdateTab(tab.id, 'order', parseInt(e.target.value))} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                        </div>
                        <div className="flex items-end justify-between gap-2">
                          <label className="flex items-center gap-2">
                            <button onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleUpdateTab(tab.id, 'hidden', !tab.hidden);
                            }} className={`relative inline-flex h-6 w-11 items-center rounded-full ${tab.hidden ? 'bg-gray-600' : 'bg-[#00bcd4]'}`}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${tab.hidden ? 'translate-x-1' : 'translate-x-6'}`} />
                            </button>
                            {tab.hidden ? <EyeOff className="w-4 h-4 text-muted" /> : <Eye className="w-4 h-4 text-[#00bcd4]" />}
                          </label>
                          <button onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteTab(tab.id);
                          }} className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'inputs' && (
            <div className="space-y-4">
              <p className="text-sm text-muted mb-4">Gérez les champs par onglet</p>
              <div className="mb-6">
                <label className="text-sm text-muted block mb-2">Sélectionner un onglet</label>
                <select value={selectedTabForInputs || ''} onChange={(e) => setSelectedTabForInputs(e.target.value)} className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground">
                  <option value="">-- Choisir --</option>
                  {config.modalTabs?.map(tab => (
                    <option key={tab.id} value={tab.id}>{tab.icon} {tab.label} ({config.formFields?.[tab.id]?.length || 0} champs)</option>
                  ))}
                </select>
              </div>
              {selectedTabForInputs && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-foreground font-semibold">Champs de {config.modalTabs?.find(t => t.id === selectedTabForInputs)?.label}</h3>
                    <button onClick={() => handleAddInput(selectedTabForInputs)} className="flex items-center gap-2 px-4 py-2 bg-[#6366f1] hover:bg-[#5558e3] text-white rounded-lg text-sm">
                      <Plus className="w-4 h-4" />Ajouter
                    </button>
                  </div>
                  <div className="space-y-2">
                    {config.formFields?.[selectedTabForInputs]?.map((input, index) => (
                      <div key={`input-${index}-${input.id}`} className="p-4 bg-background rounded-lg border border-border">
                        <div className="grid grid-cols-5 gap-4">
                          <div>
                            <label className="text-xs text-muted">ID</label>
                            <input type="text" value={input.id} onChange={(e) => handleUpdateInput(selectedTabForInputs, input.id, 'id', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-muted">Label</label>
                            <input type="text" value={input.label} onChange={(e) => handleUpdateInput(selectedTabForInputs, input.id, 'label', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-muted">Type</label>
                            <select value={input.type} onChange={(e) => handleUpdateInput(selectedTabForInputs, input.id, 'type', e.target.value)} className="w-full mt-1 bg-card border border-border rounded px-3 py-2 text-foreground text-sm">
                              <option value="text">Text</option>
                              <option value="email">Email</option>
                              <option value="tel">Téléphone</option>
                              <option value="number">Number</option>
                              <option value="date">Date</option>
                              <option value="select">Select</option>
                              <option value="radio">Radio</option>
                              <option value="checkbox">Checkbox</option>
                              <option value="toggle">Toggle</option>
                              <option value="textarea">Textarea</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 text-sm">
                              <input type="checkbox" checked={input.required || false} onChange={(e) => handleUpdateInput(selectedTabForInputs, input.id, 'required', e.target.checked)} className="w-4 h-4" />
                              Requis
                            </label>
                          </div>
                          <div className="flex items-end justify-between gap-2">
                            <label className="flex items-center gap-2">
                              <button onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleUpdateInput(selectedTabForInputs, input.id, 'hidden', !input.hidden);
                              }} className={`relative inline-flex h-6 w-11 items-center rounded-full ${input.hidden ? 'bg-gray-600' : 'bg-[#00bcd4]'}`}>
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${input.hidden ? 'translate-x-1' : 'translate-x-6'}`} />
                              </button>
                              {input.hidden ? <EyeOff className="w-4 h-4 text-muted" /> : <Eye className="w-4 h-4 text-[#00bcd4]" />}
                            </label>
                            <button onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleDeleteInput(selectedTabForInputs, input.id);
                            }} className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'status' && (
            <div className="space-y-4">
              <p className="text-sm text-muted mb-4">Mapping STATUS_BO → Colonnes</p>
              <div className="space-y-2">
                {Object.entries(config.statusMapping?.mappings || {}).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([statusBo, columnId]) => (
                  <div key={statusBo} className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
                    <div className="flex-1 grid grid-cols-3 gap-4 items-center">
                      <div>
                        <span className="text-muted text-xs">STATUS_BO</span>
                        <p className="text-foreground font-mono font-semibold text-lg">{statusBo}</p>
                      </div>
                      <div className="flex justify-center">
                        <span className="text-[#6366f1] text-xl">→</span>
                      </div>
                      <div>
                        <span className="text-muted text-xs">Colonne</span>
                        <select value={columnId} onChange={(e) => handleUpdateStatusMapping(statusBo, e.target.value)} className="w-full mt-1 bg-card border border-border rounded-lg px-3 py-2 text-foreground">
                          {config.columns?.map(col => (
                            <option key={col.id} value={col.id}>{col.title}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDeleteStatusMapping(statusBo);
                    }} className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-background rounded-lg border border-border border-dashed">
                <h3 className="text-foreground font-semibold mb-4">Ajouter mapping</h3>
                <div className="flex gap-3">
                  <input type="number" id="newStatusBo" placeholder="Ex: 7" className="flex-1 bg-card border border-border rounded-lg px-3 py-2 text-foreground" />
                  <select id="newColumn" className="flex-1 bg-card border border-border rounded-lg px-3 py-2 text-foreground">
                    <option value="">Sélectionner...</option>
                    {config.columns?.map(col => (
                      <option key={col.id} value={col.id}>{col.title}</option>
                    ))}
                  </select>
                  <button onClick={() => {
                    const statusBo = document.getElementById('newStatusBo').value;
                    const columnId = document.getElementById('newColumn').value;
                    if (statusBo && columnId) {
                      handleAddStatusMapping(statusBo, columnId);
                      document.getElementById('newStatusBo').value = '';
                      document.getElementById('newColumn').value = '';
                    }
                  }} className="px-4 py-2 bg-[#6366f1] hover:bg-[#5558e3] text-white rounded-lg flex items-center gap-2">
                    <Plus className="w-4 h-4" />Ajouter
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border bg-sidebar px-6 py-4 flex items-center justify-between">
          <button onClick={fetchConfig} className="flex items-center gap-2 px-4 py-2 hover:bg-card-hover text-muted hover:text-foreground rounded-lg">
            <RefreshCw className="w-4 h-4" />Recharger
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 hover:bg-card-hover text-muted hover:text-foreground rounded-lg">Annuler</button>
            <button onClick={handleSaveConfig} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-[#00bcd4] hover:bg-[#00acc1] disabled:bg-gray-600 text-white rounded-lg">
              {saving ? <><RefreshCw className="w-4 h-4 animate-spin" />Sauvegarde...</> : <><Save className="w-4 h-4" />Sauvegarder</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManager;
